import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  GetLocationsType,
  GetAvailableMachinesForReplaceType,
  getAvailableMachinesForReplace,
  GetBlendersType,
  GetBoardsType,
  GetLedRingsType,
  GetTubeShieldsType,
  getDiagnosis,
  getMachineServices,
  GetDiagnosisType,
  GetFlavorsType,
  getMarketingMaterials,
  GetIssueReportedType,
  getIssueReported,
  getFlavors,
  GetContractOwnerType,
  getContractOwner,
  GetCategoriesType,
  getCategories,
  getLocationForInstallation,
  getInstallationJobById,
  getVideo, // add your video API import,
  getBlenders,
  getBoards,
  getTubeShields,
  getCupHolders,
  getLedRings,
} from "../models/_publicCall";
import { fetchDataFromAPI } from "./fetchDataFromAPI";
import {
  GetMachineServiceType,
  GetMarketingType,
  GetOperationType,
} from "../models/_tickets";
import { GetCupHolderListType } from "../models/_cupHolders";
import { GetMarketingMaterialForFilterType } from "../models/_marketingMaterial";
import { InstallationJobType } from "../models/_publicCall";

interface TicketType {
  id: string;
  name: string;
}

interface HeaderType {
  id: string;
  name: string;
}

interface InstallationFormContextProps {
  locationsDB: GetLocationsType[];
  machinesDB: GetAvailableMachinesForReplaceType[];
  cupHolderDB: GetCupHolderListType[];
  ledRingsDB: GetLedRingsType[];
  machineServicesDB: GetMachineServiceType[];
  operationDB: GetOperationType[];
  marketingDB: GetMarketingType[];
  ticketTypes: TicketType[];
  headerDB: HeaderType[];
  blendersDB: GetBlendersType[];
  boardsDB: GetBoardsType[];
  tubeShieldsDB: GetTubeShieldsType[];
  materialDB: GetMarketingMaterialForFilterType[];
  issueRepotedDB: GetIssueReportedType[];
  contractOwnerDB: GetContractOwnerType[];
  diagnosis: GetDiagnosisType[];
  flavors: GetFlavorsType[];
  categoriesDB: GetCategoriesType[];
  installationJobDB: InstallationJobType | null;
  loading: boolean;
  videoOptions: { value: string; label: string }[];
  refetchBoards: () => void;
  refetchBlenders: () => void;
  refetchTubeShields: () => void;
  refetchCupHolders: () => void;
  refetchLedRings: () => void;
  fetchInstallationJob: (
    id: string,
  ) => Promise<InstallationJobType | undefined>;
  fetchVideos: () => void;
  refetchLocations: () => void;
}

const InstallationFormContext = createContext<InstallationFormContextProps>({
  cupHolderDB: [],
  fetchInstallationJob: () => Promise.resolve(undefined),
  fetchVideos: () => {},
  machineServicesDB: [],
  ledRingsDB: [],
  operationDB: [],
  marketingDB: [],
  materialDB: [],
  machinesDB: [],
  ticketTypes: [],
  headerDB: [],
  blendersDB: [],
  locationsDB: [],
  tubeShieldsDB: [],
  boardsDB: [],
  issueRepotedDB: [],
  contractOwnerDB: [],
  diagnosis: [],
  flavors: [],
  categoriesDB: [],
  installationJobDB: null,
  videoOptions: [],
  loading: false,
  refetchLocations: () => {},
  refetchBlenders: () => {},
  refetchBoards: () => {},
  refetchTubeShields: () => {},
  refetchCupHolders: () => {},
  refetchLedRings: () => {},
});

interface InstallationFormProviderProps {
  children: ReactNode;
}

export const InstalltionFormSteps = {
  CHOOSE_LOCATION: 0,
  MACHINE_SETUP: 1,
  MACHINE_DATA: 2,
  CREATE_TICKET: 3,
  LOCATION_INFORMATION: 4,
  LOCATION_CONTACTS: 5,
  PICTURES_AND_VIDEO: 6,
  SUBMIT: 7,
};

export const InstallationFormProvider: React.FC<
  InstallationFormProviderProps
> = ({ children }) => {
  const [installationJobDB, setInstallationJobDB] = useState<null>(null);
  const [machineServicesDB, setMachineServicesDB] = useState<
    GetMachineServiceType[]
  >([]);
  const [materialDB, setMaterialDB] = useState<
    GetMarketingMaterialForFilterType[]
  >([]);
  const [diagnosis, setDiagnosis] = useState<GetDiagnosisType[]>([]);
  const [machinesDB, setMachinesDB] = useState<
    GetAvailableMachinesForReplaceType[]
  >([]);
  const [locationsDB, setLocationsDB] = useState<GetLocationsType[]>([]);
  const [flavors, setFlavors] = useState<GetFlavorsType[]>([]);
  const [contractOwnerDB, setContractOwnerDB] = useState<
    GetContractOwnerType[]
  >([]);
  const [issueRepotedDB, setIssueRepotedDB] = useState<GetIssueReportedType[]>(
    [],
  );
  const [categoriesDB, setCategoriesDB] = useState<GetCategoriesType[]>([]);
  const [blendersDB, setBlendersDB] = useState<GetBlendersType[]>([]);
  const [boardsDB, setBoardsDB] = useState<GetBoardsType[]>([]);
  const [tubeShieldsDB, setTubeShieldsDB] = useState<GetTubeShieldsType[]>([]);
  const [cupHolderDB, setCupHolderDB] = useState<GetCupHolderListType[]>([]);
  const [videoOptions, setVideoOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [ledRingsDB, setLedRingsDB] = useState<GetLedRingsType[]>([]);

  const ticketTypes: TicketType[] = [
    { id: "REACTIVE_MAINTANANCE", name: "Reactive Maintanance" },
    { id: "PREVENTIVE_MAINTANANCE", name: "Preventive Maintenance" },
  ];

  const headerDB: HeaderType[] = [
    { id: "BAMBOO", name: "Bamboo" },
    { id: "NEON", name: "Neon" },
    { id: "TV", name: "TV" },
    { id: "OTHER", name: "Other" },
  ];

  // ----------------- FETCH LOCATIONS & DATA -----------------
  const fetchLocations = async () => {
    try {
      setLoading(true);

      const [
        locations,
        machines,
        issueReported,
        flavor,
        machineServices,
        diagnosisData,
        materialResult,
        contractOwner,
        categories,
        cupHolders,
        tubeShields,
        blenders,
        boards,
        ledRings,
      ] = await Promise.all([
        fetchDataFromAPI(getLocationForInstallation),
        fetchDataFromAPI(getAvailableMachinesForReplace),
        fetchDataFromAPI(getIssueReported),
        fetchDataFromAPI(getFlavors),
        fetchDataFromAPI(getMachineServices, { id: "other", name: "Other" }),
        fetchDataFromAPI(getDiagnosis),
        fetchDataFromAPI(getMarketingMaterials),
        fetchDataFromAPI(getContractOwner),
        fetchDataFromAPI(getCategories),
        fetchDataFromAPI(getCupHolders),
        fetchDataFromAPI(getTubeShields),
        fetchDataFromAPI(getBlenders),
        fetchDataFromAPI(getBoards),
        fetchDataFromAPI(getLedRings),
      ]);

      setLocationsDB(locations || []);
      setMachinesDB(machines || []);
      setIssueRepotedDB(issueReported || []);
      setMachineServicesDB(machineServices || []);
      setDiagnosis(diagnosisData || []);
      setFlavors(flavor || []);
      setMaterialDB(materialResult || []);
      setContractOwnerDB(contractOwner || []);
      setCategoriesDB(categories || []);
      setCupHolderDB(cupHolders || []);
      setTubeShieldsDB(tubeShields || []);
      setBlendersDB(blenders || []);
      setBoardsDB(boards || []);
      setLedRingsDB(ledRings || []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching locations:", err);
      setLoading(false);
    }
  };

  // ----------------- FETCH VIDEOS -----------------
  const fetchVideos = async () => {
    try {
      const response = await getVideo();
      const options = response.map((video: any) => ({
        value: video.uuid,
        label: video.fileName,
      }));
      setVideoOptions(options);
    } catch (error) {
      console.error("Error fetching videos:", error);
    }
  };

  const fetchInstallationJob = async (id: string) => {
    try {
      setLoading(true);
      const logJob = await getInstallationJobById(id);
      setInstallationJobDB(logJob);
      setLoading(false);
      return logJob;
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const refetchLocations = () => fetchLocations();
  const refetchBlenders = () => {
    fetchDataFromAPI(getBlenders).then((data) => {
      setBlendersDB(data);
    });
  };
  const refetchBoards = () => {
    fetchDataFromAPI(getBoards).then((data) => {
      setBoardsDB(data);
    });
  };
  const refetchTubeShields = () => {
    fetchDataFromAPI(getTubeShields).then((data) => {
      setTubeShieldsDB(data);
    });
  };
  const refetchCupHolders = () => {
    fetchDataFromAPI(getCupHolders).then((data) => {
      setCupHolderDB(data);
    });
  };
  const refetchLedRings = () => {
    fetchDataFromAPI(getLedRings).then((data) => {
      setLedRingsDB(data);
    });
  };

  useEffect(() => {
    fetchLocations();
    fetchVideos(); // Fetch videos on provider mount
  }, []);

  return (
    <InstallationFormContext.Provider
      value={{
        machineServicesDB,
        materialDB,
        machinesDB,
        ticketTypes,
        headerDB,
        locationsDB,
        issueRepotedDB,
        diagnosis,
        flavors,
        contractOwnerDB,
        categoriesDB,
        blendersDB,
        ledRingsDB,
        boardsDB,
        operationDB: [],
        marketingDB: [],
        cupHolderDB,
        tubeShieldsDB,
        installationJobDB,
        videoOptions,
        fetchInstallationJob,
        fetchVideos,
        loading,
        refetchLocations,
        refetchBlenders,
        refetchBoards,
        refetchTubeShields,
        refetchCupHolders,
        refetchLedRings,
      }}
    >
      {children}
    </InstallationFormContext.Provider>
  );
};

export const useInstallationForm = () => useContext(InstallationFormContext);
